import { useRef, useState, useCallback, useEffect } from 'react';
import { useLocalStorage } from 'react-haiku';
import axios from 'axios';

import { useAppStore } from 'store/useAppStore';
import { useNotificationStore } from 'store/useNotificationStore';
import { ERRORS } from 'utils/Translate';

// Example usage
// GET
// const [parameters, setParameters] = useState({ eager: 'address', name: 'Richard' });
// const [response, collect] = useCollect('users', parameters);
// useEffect(() => collect(), [collect]);

// useCollect hook implementation
// ! Requires axios@v0.22.0 or higher since AbortController is used
// ! This hook depends on REACT_APP_API and NODE_ENV env variables
// @url: string | request url ex. users/1
// @params: object | request parameters
export const useCollect = (url, params = null, cb) => {
	// Create request abort controller
	const controller = useRef(new AbortController());

	// Use local storage's token
	const [token] = useLocalStorage('token');

	// Use app's store
	const [setAuthenticated, setUser, setValid, setUpdate] = useAppStore(state => [
		state.setAuthenticated,
		state.setUser,
		state.setValid,
		state.setUpdate
	]);

	// Use notification's store
	const createNotification = useNotificationStore(state => state.createNotification);
	
	// Store response information
	const [response, setResponse] = useState({
		data: null,
		error: null,
		loading: false,
		percentage: 0
	});

	// Calculate progress percentage on upload and download
	// Works mostly only on file u/d, since api requests are not computable
	// @event: object | native onUploadProgress or onDownloadProgress event
	const onProgress = event => {
		// Get total content size
		const total = event.total || event.target.getResponseHeader('content-length');

		// Get uploaded content size
		const loaded = event.loaded;

		// Update percentage if it's calculable
		if (event.lengthComputable && total && loaded) setResponse(prev => ({
			...prev,
			percentage: Math.round((loaded * 100) / total)
		}));
	};

	// Request function with given parameters and data
	const request = useCallback(async () => {
		// Cancel previous request
		controller.current.abort();
		
		// Create new request abort controller
		controller.current = new AbortController();

		// Update response loading state
		setResponse(prev => (prev.loading ? prev : {
			...prev,
			loading: true
		}));

		try {
			// Perform axios request
			const res = await axios({
				signal: controller.current.signal,
				headers: {
					Authorization: token
				},
				method: 'GET',
				// eslint-disable-next-line
				baseURL: process.env?.REACT_APP_API,
				url,
				params,
				onUploadProgress: onProgress,
				onDownloadProgress: onProgress
			});

			// Get client version from api
			const version = res?.headers?.['client-version'];

			// Compare versions and in case of diff enable update
			if (version !== process.env.REACT_APP_VERSION) {
				setUpdate(true);
			}

			// Update valid authorization state
			setValid(true);

			// Call cb if passed
			if (cb) cb(res.data);

			// Update response by adding collected data
			// and disable loading state
			setResponse(prev => ({
				...prev,
				data: res.data,
				error: null,
				loading: false,
				percentage: 0
			}));

			// Return collected data
			return res.data;
		} catch (err) {
			// Do not proceed if request was canceled
			if (err.message === 'canceled') return;

			// Deauthenticate user if 401 received
			if (err.response?.status === 401) {
				// Update system access
				setAuthenticated(false);
				setUser({});

				// Clear local storage
				return localStorage.clear();
			}

			// Only for debugging purposes
			// eslint-disable-next-line
			if (process.env?.NODE_ENV === 'development') {
				console.error(
					`(${err.response?.status}) Request: ${url} failed!`,
					'\n',
					`Reason: ${err.response?.data?.error}`
				);
			}

			// Find error message translation
			const msg = ERRORS[err.response?.data?.error];

			// Create error notification
			if (err.response?.status === 404) {
				createNotification('invalid', msg || err.response?.data?.error);
			} else {
				createNotification('warning', msg || ERRORS.serverError);
			}

			// Update response by adding collected errors
			// and disable loading state
			setResponse(prev => ({
				...prev,
				data: null,
				error: {
					status: err.response?.status,
					message: err.response?.data
				},
				loading: false,
				percentage: 0
			}));

			// Return collected data
			return {
				data: null
			};
		}
	}, [url, params, cb]);

	// Handle request cancelation on unmount
	useEffect(() => {
		return () => controller.current.abort();
	}, []);

	return [
		[response.data, response.loading, response.error, response.percentage],
		request
	];
};